import { useMemo, useState } from 'react';
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import '../TaskBoard/task-board.scss';

type TicketType = 'supplies' | 'one_on_one' | 'labels' | 'monthly_curriculum_event' | 'request';
type MeetingWith = 'any_manager' | 'director' | 'education_director' | 'administrative_assistant';

type Props = {
  isOpen: boolean;
  teacherName: string;
  saving: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    teacherName: string;
    date: string;
    classroomNumber: string;
    ticketType: TicketType;
    suppliesData?: { whatNeed?: string; howMuchNeed?: string; whatFor?: string; needByDate?: string };
    oneOnOneData?: { notes?: string; meetingWith?: MeetingWith };
    monthlyExtrasData?: { month?: string; activities?: string };
    requestText?: string;
  }) => Promise<void>;
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const todayUs = () => {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const yyyy = now.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const TeacherTicketModal = ({ isOpen, teacherName, saving, onClose, onSubmit }: Props) => {
  const [classroomNumber, setClassroomNumber] = useState('');
  const [ticketType, setTicketType] = useState<TicketType>('supplies');
  const [whatNeed, setWhatNeed] = useState('');
  const [howMuchNeed, setHowMuchNeed] = useState('');
  const [whatFor, setWhatFor] = useState('');
  const [needByDate, setNeedByDate] = useState('');
  const [oneOnOneNotes, setOneOnOneNotes] = useState('');
  const [meetingWith, setMeetingWith] = useState<MeetingWith>('any_manager');
  const [eventMonth, setEventMonth] = useState('');
  const [activities, setActivities] = useState('');
  const [requestText, setRequestText] = useState('');
  const [labelTypeCubby, setLabelTypeCubby] = useState(false);
  const [labelTypeCrib, setLabelTypeCrib] = useState(false);
  const [labelTypeCircle, setLabelTypeCircle] = useState(false);
  const [labelTypeToy, setLabelTypeToy] = useState(false);
  const [labelTypeOther, setLabelTypeOther] = useState(false);
  const [labelTypeOtherText, setLabelTypeOtherText] = useState('');
  const [labelNamesList, setLabelNamesList] = useState('');

  const canSubmit = useMemo(() => {
    if (!(teacherName.trim().length > 0 && classroomNumber.trim().length > 0)) return false;
    if (ticketType !== 'labels') return true;
    const hasAnyType = labelTypeCubby || labelTypeCrib || labelTypeCircle || labelTypeToy || labelTypeOther;
    const hasOtherText = !labelTypeOther || labelTypeOtherText.trim().length > 0;
    const hasNames = labelNamesList.trim().length > 0;
    return hasAnyType && hasOtherText && hasNames;
  }, [
    teacherName,
    classroomNumber,
    ticketType,
    labelTypeCubby,
    labelTypeCrib,
    labelTypeCircle,
    labelTypeToy,
    labelTypeOther,
    labelTypeOtherText,
    labelNamesList,
  ]);

  const submit = async () => {
    if (!canSubmit) return;
    await onSubmit({
      teacherName: teacherName.trim(),
      date: todayIso(),
      classroomNumber: classroomNumber.trim(),
      ticketType,
      suppliesData:
        ticketType === 'supplies'
          ? {
              whatNeed: whatNeed.trim(),
              howMuchNeed: howMuchNeed.trim(),
              whatFor: whatFor.trim(),
              needByDate: needByDate || undefined,
            }
          : undefined,
      oneOnOneData:
        ticketType === 'one_on_one'
          ? {
              notes: oneOnOneNotes.trim(),
              meetingWith,
            }
          : undefined,
      monthlyExtrasData:
        ticketType === 'monthly_curriculum_event'
          ? {
              month: eventMonth.trim(),
              activities: activities.trim(),
            }
          : undefined,
      requestText:
        ticketType === 'request'
          ? requestText.trim()
          : ticketType === 'labels'
          ? [
              'Choose Type:',
              labelTypeCubby ? '- Cubby Label' : '',
              labelTypeCrib ? '- Crib Label' : '',
              labelTypeCircle ? '- Circle Time Board Stars' : '',
              labelTypeToy ? '- Toy Labels' : '',
              labelTypeOther ? `- Other: ${labelTypeOtherText.trim()}` : '',
              '',
              'List Names, Toys or anything you need labels of:',
              labelNamesList.trim(),
            ]
              .filter((line) => line !== '')
              .join('\n')
          : undefined,
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Teacher Ticket</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>Your Name</Label>
          <Input value={teacherName} disabled />
        </FormGroup>
        <FormGroup>
          <Label>Today's Date</Label>
          <Input value={todayUs()} disabled />
        </FormGroup>
        <FormGroup>
          <Label>Classroom Number</Label>
          <Input value={classroomNumber} onChange={(e) => setClassroomNumber(e.target.value)} required />
        </FormGroup>
        <FormGroup>
          <Label>Pick One</Label>
          <Input type="select" value={ticketType} onChange={(e) => setTicketType(e.target.value as TicketType)}>
            <option value="supplies">Supplies</option>
            <option value="one_on_one">One on One</option>
            <option value="labels">Labels</option>
            <option value="monthly_curriculum_event">Monthly Curriculum Event</option>
            <option value="request">Request</option>
          </Input>
        </FormGroup>

        {ticketType === 'supplies' && (
          <>
            <FormGroup>
              <Label>What do you need?</Label>
              <Input value={whatNeed} onChange={(e) => setWhatNeed(e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>How much do you need?</Label>
              <Input value={howMuchNeed} onChange={(e) => setHowMuchNeed(e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>What is it for?</Label>
              <Input value={whatFor} onChange={(e) => setWhatFor(e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>When do you need it by?</Label>
              <Input type="date" value={needByDate} onChange={(e) => setNeedByDate(e.target.value)} />
            </FormGroup>
          </>
        )}

        {ticketType === 'one_on_one' && (
          <>
            <FormGroup>
              <Label>Anything specific you want to talk about during our meeting?</Label>
              <Input
                type="textarea"
                rows={3}
                value={oneOnOneNotes}
                onChange={(e) => setOneOnOneNotes(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>Who would you like this meeting to be with?</Label>
              <Input type="select" value={meetingWith} onChange={(e) => setMeetingWith(e.target.value as MeetingWith)}>
                <option value="any_manager">Any Manager</option>
                <option value="director">Director</option>
                <option value="education_director">Education Director</option>
                <option value="administrative_assistant">Administrative Assistant</option>
              </Input>
            </FormGroup>
          </>
        )}

        {ticketType === 'monthly_curriculum_event' && (
          <>
            <FormGroup>
              <Label>What month is this for?</Label>
              <Input value={eventMonth} onChange={(e) => setEventMonth(e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label>What activities are you choosing?</Label>
              <Input value={activities} onChange={(e) => setActivities(e.target.value)} />
            </FormGroup>
          </>
        )}

        {ticketType === 'request' && (
          <FormGroup>
            <Label>Request</Label>
            <Input type="textarea" rows={4} value={requestText} onChange={(e) => setRequestText(e.target.value)} />
          </FormGroup>
        )}

        {ticketType === 'labels' && (
          <>
            <FormGroup>
              <Label className="d-block">
                <strong>Choose Type *</strong>
              </Label>
              <div className="task-board__chips mt-1">
                {(
                  [
                    {
                      key: 'cubby',
                      label: 'Cubby Label',
                      color: '#3b82f6',
                      checked: labelTypeCubby,
                      set: setLabelTypeCubby,
                    },
                    {
                      key: 'crib',
                      label: 'Crib Label',
                      color: '#8b5cf6',
                      checked: labelTypeCrib,
                      set: setLabelTypeCrib,
                    },
                    {
                      key: 'circle',
                      label: 'Circle Time Board Stars',
                      color: '#f59e0b',
                      checked: labelTypeCircle,
                      set: setLabelTypeCircle,
                    },
                    { key: 'toy', label: 'Toy Labels', color: '#10b981', checked: labelTypeToy, set: setLabelTypeToy },
                    { key: 'other', label: 'Other', color: '#6b7280', checked: labelTypeOther, set: setLabelTypeOther },
                  ] as { key: string; label: string; color: string; checked: boolean; set: (v: boolean) => void }[]
                ).map(({ key, label, color, checked, set }) => (
                  <button
                    key={key}
                    type="button"
                    className={`task-board__chip ${checked ? 'task-board__chip--active' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => set(!checked)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {labelTypeOther && (
                <Input
                  className="mt-2"
                  value={labelTypeOtherText}
                  onChange={(e) => setLabelTypeOtherText(e.target.value)}
                  placeholder="Specify other…"
                />
              )}
            </FormGroup>
            <FormGroup>
              <Label className="d-block">
                <strong>List Names, Toys or anything you need labels of *</strong>
              </Label>
              <small className="text-muted d-block mb-2">
                Do not put ALL. Check your AEA App and list the names of the children that you want a cubby label
              </small>
              <Input
                type="textarea"
                rows={4}
                value={labelNamesList}
                onChange={(e) => setLabelNamesList(e.target.value)}
                placeholder="Your answer"
              />
            </FormGroup>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="primary" onClick={() => void submit()} disabled={saving || !canSubmit}>
          Send Ticket
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TeacherTicketModal;
